/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import * as Layout from '@components/screen/Layout';
import TableTargetSetting from '@/screens/setting/target-setting/components/TableTargetSetting';
import ModalUpload from '@components/modal/ModalUpload';
import templateSalesValueNational from '@/assets/csv/template-import-target-setting-sales-value-national';
import templateSalesValueChannel from '@/assets/csv/template-import-target-setting-sales-value-channel';
import templateSalesValueBranch from '@/assets/csv/template-import-target-setting-sales-value-branch';
import templateSalesValueSalesman from '@/assets/csv/template-import-target-setting-sales-value-salesman';
import templateProductFocusSalesman from '@/assets/csv/template-import-target-setting-product-focus-salesman';
import templateDoorsNational from '@/assets/csv/template-import-target-setting-doors-national';
import RenderIf from '@components/global/RenderIf';

import {
  MONTH_NUMBERS,
  MONTH_NUMBERS_FULLNAME,
  MONTH_KEYS,
  MONTH_TABLES,
  MONTH_TABLES_WITHOUT_TOTAL,
  IGNORE_TARGET_ID,
} from '@/constants/target-settings';
import { SALESMAN_TYPE_OBJ } from '@/constants';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import {
  hasAccessEditTargetSetting,
  hasNoAccess,
  formatTargetSettingTitle,
} from '@global/helpers';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

const TABS = [
  {
    label: 'MT',
    value: 'mt',
  },
  {
    label: 'GT',
    value: 'gt',
  },
  {
    label: 'Overall',
    value: 'overall',
  },
];

const TargetSettingUploadScreen = (props) => {
  const dispatch = useDispatch();
  const showImportModalTargetSetting = useSelector(
    (state) => state.modal.showImportModalTargetSetting,
  );

  const [tableData, setTableData] = useState([]);
  const [tableStructures, setTableStructures] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('national');
  const [isShowTab, setIsShowTab] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [editing, setEditing] = useState([]);
  const [hasChanged, setHasChanged] = useState(false);
  const [targetStatus, setTargetStatus] = useState('');
  const [targetApproval, setTargetApproval] = useState('');
  const [monthName, setMonthName] = useState('');
  const [tabAfterUpload, setTabAfterUpload] = useState([]);

  const userData = useSelector((state) => state.auth.user);
  const userRole = userData?.role?.code;
  const paramType = props?.route?.params?.type;
  const paramTargetType = props?.route?.params?.targetType;
  const targetYear = props?.route?.params?.targetYear;
  const targetId = props?.route?.params?.targetId;
  const action = props?.route?.params?.action;
  const isSalesValue = Boolean(paramType == 'sales-value');
  const isDoors = Boolean(paramType == 'doors');
  const isProductFocus = Boolean(paramType == 'product-focus');
  const isSalesValueBranch =
    isSalesValue && ['branch-gt', 'branch-mt'].includes(paramTargetType);
  const isSalesValueNational = isSalesValue && paramTargetType == 'national';
  const isSalesValueChannel = isSalesValue && paramTargetType == 'channel';
  const isSalesValueSalesman = isSalesValue && paramTargetType == 'salesman';
  const isDoorsSalesman = isDoors && paramTargetType == 'salesman';
  const showUploadBtn = isSalesValueChannel
    ? activeTab === 'overall'
      ? false
      : tabAfterUpload.length > 0
      ? !tabAfterUpload.includes(activeTab)
      : false
    : false;

  const getAllTargetSettingByLevel = useApi(MsB2b.getAllTargetSettingByLevel);
  const getAllBranches = useApi(MsAccount.getAllBranches);

  const titlePage = useMemo(() => {
    return props?.route?.params?.title;
  }, [props]);

  const formattedTitleForMessage = useMemo(() => {
    return formatTargetSettingTitle({
      titlePage,
      paramType,
      paramTargetType,
      titleSection: props?.route?.params?.titleSection || '',
    });
  }, [
    titlePage,
    paramType,
    paramTargetType,
    props?.route?.params?.titleSection,
  ]);

  const targetSettingType = useMemo(() => {
    const targetTypeKey = paramTargetType.replace('-gt', '').replace('-mt', '');
    return `${paramType}-${targetTypeKey}`;
  }, [paramType, paramTargetType]);

  const customSalesTargetHeader = useMemo(() => {
    return isSalesValueBranch || isSalesValueSalesman
      ? {
          body: 'sales_target',
          label: `Sales Target ${props?.route?.params?.targetYear}`,
        }
      : ``;
  }, [monthName, props?.route?.params?.targetYear]);

  const hasAccessSubmit = hasNoAccess([
    'salesman',
    'sales_supervisor',
    'finance_supervisor',
    'marketing_support',
    'sales_supervisor',
    'branch_head',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'finance_supervisor',
    'beauty_advisor',
    'merchandiser',
    '3pl',
    'supervisor_logistic',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'general_manager_marketing',
    'marketing_admin',
    'marketing_support',
    'regional_distributor_manager',
  ]);

  const showApproval = useMemo(() => {
    let isShowApproval =
      targetStatus == 'need_approval' && targetApproval == userRole;
    if (
      paramType == 'sales-value' &&
      paramTargetType == 'salesman' &&
      targetId &&
      !IGNORE_TARGET_ID.includes(targetId) &&
      userData?.sales_team?.length
    ) {
      isShowApproval =
        isShowApproval && userData.sales_team.find((s) => s.id == targetId);
    }
    return isShowApproval;
  }, [targetStatus, targetApproval, targetId, paramType, paramTargetType]);

  const csvTemplate = useMemo(() => {
    const csvTemplateMap = {
      'sales-value-national': templateSalesValueNational,
      'sales-value-channel': templateSalesValueChannel,
      'sales-value-branch': templateSalesValueBranch,
      'sales-value-salesman': templateSalesValueSalesman,

      'product-focus-salesman': templateProductFocusSalesman,

      'doors-national': templateDoorsNational,
    };

    return csvTemplateMap[targetSettingType];
  }, [targetSettingType]);

  const csvName = useMemo(() => {
    const csvNameMap = {
      'sales-value-national':
        'import-template-target-setting-sales-value-national',
      'sales-value-channel':
        'import-template-target-setting-sales-value-channel',
      'sales-value-branch': 'import-template-target-setting-sales-value-branch',
      'sales-value-salesman':
        'import-template-target-setting-sales-value-salesman',

      'product-focus-salesman':
        'import-template-target-setting-product-focus-salesman',

      'doors-national': 'import-template-target-setting-doors-national',
    };

    return csvNameMap[targetSettingType];
  }, [targetSettingType]);

  const csvValidHeader = useMemo(() => {
    const csvValidHeaderMap = {
      'sales-value-national': [
        'brand_id',
        'month',
        'year',
        'value',
        'value_type',
      ],
      'sales-value-channel': [
        'market_type',
        'brand_id',
        'month',
        'year',
        'value',
        'value_type',
      ],
      'sales-value-branch': [
        'market_type_id',
        'branch_id',
        'month',
        'year',
        'value',
        'value_type',
      ],
      'sales-value-salesman': [
        'sales_team_id',
        'email',
        'month',
        'year',
        'value',
        'value_type',
      ],

      'product-focus-salesman': [
        'salesman_type',
        'brand_id_focus_1',
        'sku_1',
        'value_1',
        'value_type_1',
        'brand_id_focus_2',
        'sku_2',
        'value_2',
        'value_type_2',
        'brand_id_focus_3',
        'sku_3',
        'value_3',
        'value_type_3',
        'month',
        'year',
      ],

      'doors-national': [
        'brand_id',
        'sku',
        'month',
        'year',
        'value',
        'value_type',
      ],
    };

    return csvValidHeaderMap[targetSettingType];
  }, [targetSettingType]);

  const convertDetailValue = (item) => {
    const targetTypeKey = paramTargetType.replace('-gt', '').replace('-mt', '');
    let sub_level = '';
    if (isSalesValueBranch || isProductFocus) {
      if (paramTargetType?.indexOf('gt') > -1) {
        sub_level = 'gt';
      } else if (paramTargetType?.indexOf('mt') > -1) {
        sub_level = 'mt';
      }
    }

    const default_payload = {
      year: Number(targetYear),
      month: item.month,
      target_type: paramType,
      level: targetTypeKey,
    };

    return {
      'sales-value-national': {
        ...default_payload,
        brand: item.brand,
        value: '',
        target_value_type: 'amount',
      },
      'sales-value-branch': {
        ...default_payload,
        branch: item.branch,
        market_type: item.market_type,
        value: '',
        type: sub_level,
      },
      'sales-value-channel': {
        ...default_payload,
        brand: item.brand,
        market_type: item.market_type,
        value: '',
      },
      'sales-value-salesman': {
        ...default_payload,
        sales_team: item.sales_team,
        salesman_type: item.salesman_type,
        salesman_name: item.salesman_name,
        sales_code: item.sales_code,
        target_value: '',
        target_value_type: 'amount',
      },
      'doors-national': {
        ...default_payload,
        brand: item.brand,
        product: item.product || {},
        sku: item.sku,
        target_value: '',
      },
      'product-focus-salesman': {
        ...default_payload,
        salesman_type: item.salesman_type,
        brand_id_focus_1: item.brand_id_focus_1 || '',
        brand_id_focus_2: item.brand_id_focus_2 || '',
        brand_id_focus_3: item.brand_id_focus_3 || '',
        sku_1: item.sku_1 || '',
        sku_2: item.sku_2 || '',
        sku_3: item.sku_3 || '',
        target_value_type_1: 'amount',
        target_value_type_2: 'amount',
        target_value_type_3: 'amount',
        value_1: item.value_1 || '',
        value_2: item.value_2 || '',
        value_3: item.value_3 || '',
        product_1: item.product_1 || {},
        product_2: item.product_2 || {},
        product_3: item.product_3 || {},
        type: item.sub_level || '',
      },
    };
  };

  const convertValue = (data = []) => {
    let result = [];

    if (isProductFocus) {
      if (data?.length) {
        const unique_array = [];

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          let sub_level = '';
          if (paramTargetType?.indexOf('gt') > -1) {
            sub_level = 'gt';
          } else if (paramTargetType?.indexOf('mt') > -1) {
            sub_level = 'mt';
          }
          if (
            item.salesman_type &&
            unique_array.findIndex((s) => s.code == item.salesman_type) == -1 &&
            item.sub_level == sub_level
          ) {
            const obj = {
              name: SALESMAN_TYPE_OBJ[item.salesman_type],
              code: item.salesman_type,
              data: [],
              sub_level,
              detail: convertDetailValue(item),
            };
            for (let j = 1; j <= 3; j++) {
              const obj_brand = {
                salesman_type: `Brand Focus ${j}`,
              };
              const obj_sku = {
                salesman_type: 'SKU',
              };
              const obj_target = {
                salesman_type: 'Target',
                data: {},
                detail: obj.detail,
                month_detail: {},
              };
              const obj_empty = {
                salesman_type: '--',
              };

              for (let k = 0; k < MONTH_KEYS.length; k++) {
                const month = MONTH_KEYS[k];
                const findData = data.find(
                  (a) =>
                    a.month == k + 1 &&
                    a.salesman_type == obj.code &&
                    obj.sub_level == sub_level,
                );

                obj_brand[month] =
                  findData &&
                  findData[`brand_id_focus_${j}`] &&
                  findData[`brand_id_focus_${j}`]?.name
                    ? findData[`brand_id_focus_${j}`]['name']
                    : '-';
                obj_brand['total'] = '-';

                obj_sku[month] =
                  findData && findData[`sku_${j}`] ? findData[`sku_${j}`] : '-';
                obj_sku['total'] = '-';

                obj_target[month] =
                  findData && findData[`value_${j}`]
                    ? findData[`value_${j}`]
                    : 0;
                obj_target['data'][month] = findData;
                obj_target['month_detail'][month] = convertDetailValue(
                  findData || {},
                );
                obj_target['field'] = `value_${j}`;
                obj_target['total'] =
                  (obj_target['total'] || 0) + obj_target[month];

                obj_empty[month] = '--';
                obj_empty['total'] = '--';
              }

              obj.data.push(obj_brand);
              obj.data.push(obj_sku);
              obj.data.push(obj_target);
              obj.data.push(obj_empty);
            }
            unique_array.push(obj);
          }
        }

        result = unique_array;
      }
    } else {
      const brands = [];
      const isOverallChannel = isSalesValueChannel && activeTab == 'overall';

      for (const item of data) {
        if (item?.salesman_type) {
          item.salesman_type = item.salesman_type.replace('\n', '');
        }
        const obj = {
          id: item?._id,
          object_id: isSalesValueSalesman ? item?._id : item?.brand?.id,
          sales_code: isSalesValueSalesman ? item.sales_code : '',
          brand: item?.brand?.name,
          salesman_code: item?.sales_code,
          salesman_name: item?.salesman_name ? item?.salesman_name : '',
          sku: item?.sku,
          total: 0,
          detail: convertDetailValue(item),
        };
        if (isSalesValueChannel) {
          obj['market_type'] = item?.market_type?.code;
        }
        const hasNoSameBrand =
          brands.findIndex((b) => {
            if (isSalesValueSalesman) {
              return b.sales_code == obj.sales_code;
            }
            return (
              b.object_id == obj.object_id &&
              (isDoors ? b.sku == obj.sku : true)
            );
          }) == -1;
        if (hasNoSameBrand || isOverallChannel) {
          brands.push(obj);
        }
      }

      if (brands?.length) {
        const total_target = {
          brand: 'Total',
          ...(isDoorsSalesman || isSalesValueSalesman
            ? {
                salesman_code: 'Total',
              }
            : {}),
        };
        for (const item of brands) {
          const obj = JSON.parse(JSON.stringify(item));
          const check = data
            .filter((b) => {
              if (isSalesValueSalesman) {
                return b?.sales_code == item.sales_code;
              } else if (isDoors) {
                return b?.brand?.id == item.object_id && b.sku == item.sku;
              } else {
                return b?.brand?.id == item.object_id;
              }
            })
            .sort((a, b) => {
              return a?.month - b?.month;
            });
          // if (isSalesValueSalesman) {
          //   for (let j = 0; j < check.length; j++) {
          //     const child = check[j];
          //     const obj_value = child?.target_value || 0;
          //     obj['total'] = (obj['total'] || 0) + obj_value;
          //     obj['sales_target'] = obj_value;
          //     obj['data'] = {
          //       sales_target: {
          //         ...item,
          //         _id: item?.id,
          //       },
          //     };
          //   }
          // } else {
          obj['data'] = {};
          obj['split'] = {};
          for (let j = 1; j <= 12; j++) {
            const child = check?.find((c) => c?.month == j);
            const split_value = {
              mt: 0,
              gt: 0,
            };
            if (isOverallChannel) {
              const checkValueMt = check?.find(
                (c) =>
                  c?.month == j && c?.market_type?.code.toLowerCase() == 'mt',
              );
              if (checkValueMt) {
                split_value['mt'] =
                  checkValueMt?.target_value &&
                  !isNaN(Number(checkValueMt.target_value))
                    ? Number(checkValueMt.target_value)
                    : 0;
              }
              const checkValueGt = check?.find(
                (c) =>
                  c?.month == j && c?.market_type?.code.toLowerCase() == 'gt',
              );
              if (checkValueGt) {
                split_value['gt'] =
                  checkValueGt?.target_value &&
                  !isNaN(Number(checkValueGt.target_value))
                    ? Number(checkValueGt.target_value)
                    : 0;
              }
            }
            obj['split'][MONTH_NUMBERS[j]] = split_value;
            if (child?.target_value && !isNaN(Number(child.target_value))) {
              if (isOverallChannel) {
                obj[MONTH_NUMBERS[j]] = Number(child.target_value);
                obj['data'][MONTH_NUMBERS[j]] = child || null;
              } else {
                obj[MONTH_NUMBERS[j]] = Number(child.target_value);
                obj['data'][MONTH_NUMBERS[j]] = child || null;
              }
            } else {
              obj[MONTH_NUMBERS[j]] = 0;
              obj['data'][MONTH_NUMBERS[j]] = child || null;
            }

            const obj_value = obj[MONTH_NUMBERS[j]];
            obj['total'] = (obj['total'] || 0) + obj_value;
            // isSalesValueBranch || isSalesValueSalesman
            obj['sales_target'] = (obj['sales_target'] || 0) + obj_value;

            total_target[MONTH_NUMBERS[j]] =
              (total_target[MONTH_NUMBERS[j]] || 0) + obj_value;
            total_target['total'] = (total_target['total'] || 0) + obj_value;
            total_target['sales_target'] =
              (total_target['sales_target'] || 0) + obj_value;
          }
          // }
          result.push(obj);
        }
        if (
          (isSalesValueChannel || isSalesValueSalesman || isSalesValueBranch) &&
          activeTab != 'overall'
        ) {
          result.push(total_target);
        }
      }
    }

    return result;
  };

  const doUpload = async (url) => {
    const failedProcess = (message = '') => {
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title: 'Error',
          description: message || 'A system error occurred',
        }),
      );
      dispatch(showPopup({ name: 'isLoading', value: false }));
    };
    try {
      dispatch(
        showPopup({
          name: 'showImportModalTargetSetting',
          value: false,
        }),
      );
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const type = props?.route?.params?.type;
      const targetType = props?.route?.params?.targetType
        .replace('-gt', '')
        .replace('-mt', '');
      const payload = {
        url: url,
        target_type: type,
        level: targetType,
        year: targetYear,
      };
      if (isSalesValueBranch || isProductFocus) {
        if (paramTargetType?.indexOf('gt') > -1) {
          payload['type'] = 'gt';
        } else if (paramTargetType?.indexOf('mt') > -1) {
          payload['type'] = 'mt';
        }
      }
      if (targetId && !IGNORE_TARGET_ID.includes(targetId)) {
        payload['team_id'] = targetId;
      }
      const response = await MsB2b.uploadTargetSetting(payload);
      if (response.status == 200) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        setHasData(true);
        setHasChanged(true);
        if (isSalesValueChannel) {
          setTabAfterUpload((prev) => [...prev, activeTab]);
        }
        doSearch(true);
        dispatch(
          showAlert({
            show: true,
            isConfirm: false,
            title: 'Information',
            description: 'Upload target setting success',
            onCallback: () => {
              props.navigation.navigate('TargetSettingUploadScreen', {
                ...props?.route?.params,
                action: 'view',
              });
            },
          }),
        );
      } else {
        failedProcess();
      }
    } catch (error) {
      console.log(error);
      failedProcess(error?.response?.data?.message);
    }
  };

  const doSearch = async () => {
    setMonthName('');
    setTargetStatus('');
    setTargetApproval('');
    setLoading(true);
    try {
      const type = props?.route?.params?.type;
      const targetType = props?.route?.params?.targetType
        .replace('-gt', '')
        .replace('-mt', '');
      let response = null;
      const filter = {
        year: targetYear,
      };
      if (isSalesValueBranch || isProductFocus) {
        if (paramTargetType?.indexOf('gt') > -1) {
          filter['type'] = 'gt';
        } else if (paramTargetType?.indexOf('mt') > -1) {
          filter['type'] = 'mt';
        }
      }
      if (isSalesValueChannel) {
        if (['mt', 'gt', 'national'].includes(activeTab)) {
          const marketCode = activeTab == 'national' ? 'mt' : activeTab;
          filter['market_type.code'] = marketCode;
        }
      }
      if (targetId && !IGNORE_TARGET_ID.includes(targetId)) {
        filter['team_id'] = targetId;
      }
      if (isSalesValueBranch) {
        response = await getAllBranches.request({
          params: {
            filter: {
              is_active: true,
            },
            sort: 'name',
          },
        });
      } else {
        response = await getAllTargetSettingByLevel.request(type, targetType, {
          params: {
            filter,
            sort:
              targetType === 'national' || targetType === 'channel'
                ? 'brand.name'
                : 'sales_code',
          },
        });
      }
      if (response?.data?.data?.length) {
        const res = response?.data?.data;
        if (res[0]?.current_status) {
          setTargetStatus(res[0]?.current_status);
        }
        if (res[0]?.current_approval) {
          setTargetApproval(res[0]?.current_approval);
        }
        let result = [];
        const overall_result = [];
        if (isSalesValueBranch) {
          const child_response = await getAllTargetSettingByLevel.request(
            type,
            targetType,
            {
              params: {
                filter,
              },
            },
          );
          const temps = child_response?.data?.data?.length
            ? child_response.data.data
            : [];
          if (temps[0]?.current_status) {
            setTargetStatus(temps[0].current_status);
            if (temps[0]?.current_status == 'draft') {
              setHasChanged(true);
            }
          }
          if (temps[0]?.current_approval) {
            setTargetApproval(temps[0].current_approval);
          }
          const code = props?.route?.params?.targetType?.replace('branch-', '');
          if (
            temps?.length &&
            Boolean(
              temps.find(
                (a) =>
                  a?.market_type?.code?.toLowerCase() == code.toLowerCase(),
              ),
            )
          ) {
            setHasData(true);
            if (temps?.[0]?.month) {
              setMonthName(MONTH_NUMBERS_FULLNAME[temps?.[0]?.month]);
            }
          }
          const total_target = {
            branch: 'Total',
          };
          for (let i = 0; i < res.length; i++) {
            const item = res[i];
            const temp = temps.find((b) => b?.branch?.id);
            const selected = temps.filter((b) => b?.branch?.id == item?._id);
            const obj = {
              id: item?._id,
              branch: item?.name,
              data: {},
              // sales_target:
              //   selected?.target_value && !isNaN(Number(selected.target_value))
              //     ? Number(selected.target_value)
              //     : 0,
              // data: {
              //   sales_target: selected || {},
              // },
              // detail: convertDetailValue({
              //   ...item,
              //   ...(selected || {}),
              //   month: selected?.month ? selected.month : temp?.month,
              //   branch: {
              //     id: selected?.branch?.id ? selected.branch.id : item?._id,
              //     name: selected?.branch?.name
              //       ? selected.branch.name
              //       : item?.name,
              //   },
              //   market_type: {
              //     id: selected?.market_type?.id
              //       ? selected.market_type.id
              //       : temp?.market_type?.id,
              //     code: selected?.market_type?.code
              //       ? selected.market_type.code
              //       : temp?.market_type?.code,
              //   },
              // }),
            };
            for (let j = 1; j <= 12; j++) {
              const month_child = selected?.find((c) => c?.month == j);
              obj[MONTH_NUMBERS[j]] =
                month_child?.target_value &&
                !isNaN(Number(month_child.target_value))
                  ? Number(month_child.target_value)
                  : 0;
              obj['data'][MONTH_NUMBERS[j]] = month_child || null;
              obj['detail'] = convertDetailValue({
                ...item,
                ...(month_child || {}),
                month: month_child?.month ? month_child.month : temp?.month,
                branch: {
                  id: month_child?.branch?.id
                    ? month_child.branch.id
                    : item?._id,
                  name: month_child?.branch?.name
                    ? month_child.branch.name
                    : item?.name,
                },
                market_type: {
                  id: month_child?.market_type?.id
                    ? month_child.market_type.id
                    : temp?.market_type?.id,
                  code: month_child?.market_type?.code
                    ? month_child.market_type.code
                    : temp?.market_type?.code,
                },
              });

              const obj_value = obj[MONTH_NUMBERS[j]];
              obj['total'] = (obj['total'] || 0) + obj_value;

              total_target[MONTH_NUMBERS[j]] =
                (total_target[MONTH_NUMBERS[j]] || 0) + obj_value;
              total_target['total'] = (total_target['total'] || 0) + obj_value;
            }
            result.push(obj);
          }
          result.push(total_target);
        } else {
          if (res?.length) {
            setHasData(true);
            if (res?.[0]?.month) {
              setMonthName(MONTH_NUMBERS_FULLNAME[res?.[0]?.month]);
            }
          }
          result = convertValue(res);
          if (isSalesValueChannel && result?.length) {
            // overall_result
            const obj_overall = {
              mt: {
                brand: 'Total MT',
              },
              gt: {
                brand: 'Total GT',
              },
              total: {
                brand: 'Total Target',
              },
            };
            const done = [];
            for (let k = 0; k < result.length; k++) {
              const item = result[k];
              if (
                done.findIndex(
                  (a) =>
                    a.name == item?.brand && a.market_type == item?.market_type,
                ) == -1
              ) {
                done.push({
                  name: item?.brand,
                  market_type: item?.market_type,
                });
                for (let j = 0; j < MONTH_KEYS.length; j++) {
                  const month = MONTH_KEYS[j];
                  const total_value =
                    item['split']?.[month]?.[item?.market_type.toLowerCase()] ||
                    0;
                  const marketType = item?.market_type?.toLowerCase();

                  if (marketType === 'mt') {
                    obj_overall['mt'][month] =
                      (obj_overall['mt'][month] || 0) + total_value;
                    obj_overall['mt']['total'] =
                      (obj_overall['mt']['total'] || 0) + total_value;
                  } else if (marketType === 'gt') {
                    obj_overall['gt'][month] =
                      (obj_overall['gt'][month] || 0) + total_value;
                    obj_overall['gt']['total'] =
                      (obj_overall['gt']['total'] || 0) + total_value;
                  } else {
                    // obj_overall['gt'][month] = obj_overall['gt'][month] || 0;
                    // obj_overall['gt']['total'] = obj_overall['gt']['total'] || 0;
                    // obj_overall['mt'][month] = obj_overall['mt'][month] || 0;
                    // obj_overall['mt']['total'] = obj_overall['mt']['total'] || 0;
                  }

                  obj_overall['total'][month] =
                    (obj_overall['mt'][month] || 0) +
                    (obj_overall['gt'][month] || 0);
                  obj_overall['total']['total'] =
                    (obj_overall['mt']['total'] || 0) +
                    (obj_overall['gt']['total'] || 0);
                }
              }
            }
            overall_result.push(obj_overall.mt);
            overall_result.push(obj_overall.gt);
            overall_result.push(obj_overall.total);
          }
        }

        if (isSalesValueChannel) {
          if (activeTab == 'overall') {
            setTableData(overall_result);
          } else {
            setTableData(result);
          }
        } else {
          if (isSalesValueNational || isDoors) {
            if (result?.length) {
              const obj = {
                brand: 'Total',
              };
              const keys = [...MONTH_KEYS, 'total'];
              for (let i = 0; i < result.length; i++) {
                const item = result[i];
                for (let j = 0; j < keys.length; j++) {
                  const child = keys[j];
                  const total = obj[child] || 0;
                  const child_total = item[child] || 0;
                  obj[child] = total + child_total;
                }
              }
              result.push(obj);
            }
          }
          setTableData(result);
        }
      } else {
        setTableData([]);
      }
      setLoading(false);
    } catch (err) {
      console.log('err', err);
      setLoading(false);
    }
  };

  const onSubmit = async (status = 'draft') => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const payload = {
        year: targetYear,
      };
      if (isSalesValueBranch || isProductFocus) {
        if (paramTargetType?.indexOf('mt') > -1) {
          payload['type'] = 'mt';
        } else if (paramTargetType?.indexOf('gt') > -1) {
          payload['type'] = 'gt';
        }
      }
      if (targetId && !IGNORE_TARGET_ID.includes(targetId)) {
        payload['team_id'] = targetId;
      }
      const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');
      await MsB2b.updateStatusTargetSetting(
        paramType,
        targetType,
        status,
        payload,
      );
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title:
            status === 'approved'
              ? `${formattedTitleForMessage} target Approved`
              : status === 'rejected'
              ? `${formattedTitleForMessage} target Rejected`
              : status == 'draft'
              ? `${titlePage} target Save as Draft`
              : `${titlePage} target Submitted`,
          description:
            status === 'approved'
              ? `${formattedTitleForMessage} target has been approved.`
              : status === 'rejected'
              ? `${formattedTitleForMessage} target has been rejected.`
              : status == 'draft'
              ? 'Successfully saved data.'
              : `${titlePage} target submitted, you'll be notified once it has been approved.`,
          onCallback: () => {
            doSearch();
          },
        }),
      );
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred.',
        }),
      );
    }
  };

  const onEdit = ({ event, index, childIndex }) => {
    if (event.detail == 2) {
      const newEdtiting = JSON.parse(JSON.stringify(editing));
      if (
        newEdtiting.indexOf(`body-${index}-${childIndex}`) == -1 &&
        childIndex > 0
      ) {
        newEdtiting.push(`body-${index}-${childIndex}`);
        setEditing(newEdtiting);
      }
    }
  };

  const onChangeTab = () => {
    const targetYearTable = props?.route?.params?.targetYear;
    const DEFAULT_STRUCTURE = [
      [
        { label: 'Brand', body: 'brand', rowSpan: 2 },
        {
          label: `Sales Target ${targetYearTable}`,
          body: 'sales_target',
          colSpan: MONTH_TABLES.length,
        },
      ],
      MONTH_TABLES,
    ];

    const TABLE_HEADER_STRUCTURES = {
      'sales-value': {
        national: DEFAULT_STRUCTURE,
        channel: {
          mt: DEFAULT_STRUCTURE,
          gt: DEFAULT_STRUCTURE,
          overall: DEFAULT_STRUCTURE,
        },
        branch: [
          [
            { label: 'Branch', body: 'branch', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
        salesman: [
          [
            { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
            { label: 'Salesman Name', body: 'salesman_name', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
      },
      'product-focus': {
        salesman: [
          [
            { label: 'Sales Type', body: 'salesman_type', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES_WITHOUT_TOTAL,
        ],
      },
      doors: {
        national: [
          [
            { label: 'Brand', body: 'brand', rowSpan: 2 },
            { label: 'SKU Reference', body: 'sku', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
        salesman: [
          [
            { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
            { label: 'Salesman Type', body: 'salesman_type', rowSpan: 2 },
            {
              label: `Sales Target ${targetYearTable}`,
              body: 'sales_target',
              colSpan: MONTH_TABLES.length,
            },
          ],
          MONTH_TABLES,
        ],
      },
    };

    const DEFAULT_STRUCTURE_BODY = [
      { label: 'Brand', body: 'brand' },
      ...MONTH_TABLES,
    ];
    const TABLE_BODY_STRUCTURES = {
      'sales-value': {
        national: DEFAULT_STRUCTURE_BODY,
        channel: {
          mt: DEFAULT_STRUCTURE_BODY,
          gt: DEFAULT_STRUCTURE_BODY,
          overall: DEFAULT_STRUCTURE_BODY,
        },
        branch: [
          { label: 'Branch', body: 'branch' },
          // {
          //   label: `Sales Target ${targetYearTable}`,
          //   body: 'sales_target',
          // },
          ...MONTH_TABLES,
        ],
        salesman: [
          { label: 'Salesman Code', body: 'salesman_code' },
          { label: 'Salesman Name', body: 'salesman_name' },
          // {
          //   label: `Sales Target ${targetYearTable}`,
          //   body: 'sales_target',
          // },
          ...MONTH_TABLES,
        ],
      },
      'product-focus': {
        salesman: [
          { label: 'Sales Type', body: 'salesman_type', rowSpan: 2 },
          ...MONTH_TABLES_WITHOUT_TOTAL,
        ],
      },
      doors: {
        national: [
          { label: 'Brand', body: 'brand', rowSpan: 2 },
          { label: 'SKU Reference', body: 'sku', rowSpan: 2 },
          ...MONTH_TABLES,
        ],
        salesman: [
          { label: 'Salesman Code', body: 'salesman_code', rowSpan: 2 },
          { label: 'Salesman Type', body: 'salesman_type', rowSpan: 2 },
          ...MONTH_TABLES,
        ],
      },
    };

    const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');

    if (isSalesValueChannel) {
      const activeSubType = ['mt', 'gt', 'overall'].includes(activeTab)
        ? activeTab
        : 'mt';
      setTableHeader(
        TABLE_HEADER_STRUCTURES[paramType][targetType][activeSubType],
      );
      setTableStructures(
        TABLE_BODY_STRUCTURES[paramType][targetType][activeSubType],
      );
    } else {
      setTableHeader(TABLE_HEADER_STRUCTURES[paramType][targetType]);
      setTableStructures(TABLE_BODY_STRUCTURES[paramType][targetType]);
    }
    setEditing([]);
    setHasChanged(false);
    doSearch();
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && !loading) {
      onChangeTab();
    }
    return () => {
      mounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setHasChanged(false);
      setEditing([]);
      setHasData(false);
      const targetType = paramTargetType.replace('-gt', '').replace('-mt', '');
      const showButton = hasAccessEditTargetSetting(paramType, targetType);
      setHasAccess(showButton);
      let newActiveTab = targetType;
      if (targetType == 'channel') {
        newActiveTab = 'mt';
        setActiveTab(newActiveTab);
        setIsShowTab(true);
      } else {
        newActiveTab = targetType || 'national';
        setActiveTab(newActiveTab);
        setIsShowTab(false);
      }

      // Sometimes activeTab state not resetting, force onChangeTab
      if (activeTab == newActiveTab) {
        onChangeTab();
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const disabledSubmit = useMemo(() => {
    if (tabAfterUpload.length > 0) {
      return false;
    }
    if (action == 'edit') {
      return true;
    }
    return !hasData || targetStatus == 'need_approval';
  }, [hasData, hasChanged, editing, targetStatus, action]);

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Back
            title="Target Setting"
            onCallback={() => props.navigation.navigate('TargetSettingScreen')}
          />
          <Layout.Title title={props?.route?.params?.title}>
            <View style={styles.submitWrapper}>
              {showApproval && (
                <>
                  <View style={styles.buttonWrapper}>
                    <Button
                      rounded
                      type="danger"
                      buttonStyle={styles.buttonStyle}
                      title={
                        <SociollaText type="medium" style={styles.buttonText}>
                          Reject
                        </SociollaText>
                      }
                      onPress={() => onSubmit('rejected')}
                    />
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button
                      rounded
                      buttonStyle={styles.buttonStyle}
                      title={
                        <SociollaText type="medium" style={styles.buttonText}>
                          Approve
                        </SociollaText>
                      }
                      onPress={() => onSubmit('approved')}
                    />
                  </View>
                </>
              )}
              <RenderIf condition={!showApproval}>
                <RenderIf
                  condition={
                    hasAccess &&
                    (targetStatus ? targetStatus != 'need_approval' : true)
                  }>
                  <View style={styles.buttonWrapper}>
                    <Button
                      rounded
                      type="default"
                      buttonStyle={styles.buttonStyle}
                      disabledStyle={styles.buttonStyle}
                      title={
                        <SociollaText
                          type="medium"
                          style={{
                            fontSize: 14,
                            color: Colors.black,
                          }}>
                          Upload
                        </SociollaText>
                      }
                      onPress={() => {
                        dispatch(
                          showPopup({
                            name: 'showImportModalTargetSetting',
                            value: true,
                          }),
                        );
                      }}
                    />
                  </View>
                </RenderIf>
                <RenderIf condition={hasAccessSubmit}>
                  <View style={styles.buttonWrapper}>
                    <Button
                      rounded
                      type="primary"
                      buttonStyle={styles.buttonStyle}
                      disabledStyle={styles.buttonStyle}
                      disabled={disabledSubmit}
                      title={
                        <SociollaText type="medium" style={styles.buttonText}>
                          Save Draft
                        </SociollaText>
                      }
                      onPress={() => onSubmit('draft')}
                    />
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button
                      rounded
                      buttonStyle={styles.buttonStyle}
                      disabledStyle={styles.buttonStyle}
                      disabled={disabledSubmit}
                      title={
                        <SociollaText type="medium" style={styles.buttonText}>
                          Submit
                        </SociollaText>
                      }
                      onPress={() => onSubmit('need_approval')}
                    />
                  </View>
                </RenderIf>
              </RenderIf>
            </View>
          </Layout.Title>

          {props?.route?.params?.titleSection ? (
            <SociollaText style={styles.titleSubSection}>
              {props.route.params.titleSection}
            </SociollaText>
          ) : (
            false
          )}

          {isShowTab ? (
            <View style={styles.tabWrapper}>
              {TABS.map((item, idx) => (
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={[
                    styles.tabItem,
                    activeTab == item.value ? styles.tabItemActive : {},
                  ]}
                  onPress={() => {
                    setActiveTab(item.value);
                  }}
                  key={idx}>
                  <SociollaText
                    type={activeTab == item.value ? 'medium' : 'regular'}
                    style={styles.tabItemText}>
                    {item.label}
                  </SociollaText>
                </TouchableOpacity>
              ))}
            </View>
          ) : (
            false
          )}

          <Layout.Wrapper fullWidth rounded>
            {loading ? (
              <Layout.Loader />
            ) : hasData && action == 'view' && !showUploadBtn ? (
              <>
                <View style={styles.resultContainer}>
                  {isProductFocus ? (
                    <>
                      {tableData.map((item, idx) => {
                        return (
                          <TableTargetSetting
                            key={idx}
                            index={idx}
                            dispatch={dispatch}
                            prefixHeader={item?.name}
                            tableHeader={tableHeader}
                            tableStructures={tableStructures}
                            tableData={tableData}
                            editing={editing}
                            disabledEdit={
                              activeTab == 'overall' ||
                              targetStatus == 'need_approval'
                            }
                            targetSettingType={targetSettingType}
                            setTableData={setTableData}
                            setHasChanged={setHasChanged}
                            setEditing={setEditing}
                            onEdit={onEdit}
                          />
                        );
                      })}
                    </>
                  ) : (
                    <TableTargetSetting
                      index={-1}
                      dispatch={dispatch}
                      tableHeader={tableHeader}
                      tableStructures={tableStructures}
                      tableData={tableData}
                      editing={editing}
                      disabledEdit={
                        activeTab == 'overall' ||
                        targetStatus == 'need_approval'
                      }
                      targetSettingType={targetSettingType}
                      customSalesTargetHeader={customSalesTargetHeader}
                      setTableData={setTableData}
                      setHasChanged={setHasChanged}
                      setEditing={setEditing}
                      onEdit={onEdit}
                    />
                  )}
                </View>
              </>
            ) : (
              <View style={styles.wrapper}>
                <SociollaText style={styles.formText}>
                  Target setting file
                </SociollaText>

                {hasAccess ? (
                  <></>
                ) : (
                  <SociollaText>You have no permission.</SociollaText>
                )}
              </View>
            )}
          </Layout.Wrapper>
        </>
      ) : (
        false
      )}
      {showImportModalTargetSetting ? (
        <ModalUpload
          show={showImportModalTargetSetting}
          onClose={() =>
            dispatch(
              showPopup({
                name: 'showImportModalTargetSetting',
                value: false,
              }),
            )
          }
          templateUrl={csvTemplate}
          onUpload={doUpload}
          autoUploadS3={true}
          name={csvName}
          validHeader={csvValidHeader}
        />
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  resultContainer: {
    width: '100%',
  },
  wrapper: {
    width: '100%',
    minHeight: 400,
    padding: 15,
  },
  buttonWrapper: {
    marginLeft: 15,
  },
  buttonStyle: {
    width: 160,
  },
  buttonText: {
    fontSize: 14,
    color: Colors.white,
  },
  formText: {
    marginBottom: 15,
  },
  submitWrapper: {
    flexDirection: 'row',
  },
  tabWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 5,
    backgroundColor: Colors.white,
    marginBottom: 20,
  },
  tabItem: {
    minWidth: 70,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
    borderBottomColor: Colors.transparent,
    borderBottomWidth: 2,
  },
  tabItemActive: { borderBottomColor: Colors.blue0 },
  tabItemText: { textAlign: 'center' },
  titleSubSection: { fontSize: 16, marginBottom: 25 },
});

export default TargetSettingUploadScreen;
